import { Column as PrimeColumn } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tooltip } from "primereact/tooltip";
import { classNames as joinClassNames } from "primereact/utils";

import { Loading } from "../../loading";
import { Paginator } from "../../paginator";
import { useHubTableContext } from "../hub-table.context";
import styles from "../hub-table.module.scss";
import { HubTableEmptyDefault } from "./view-empty-default";
import { HubTableLoadingAnnouncement } from "./view-loading-announcement";
import { HubTableScrollGutter } from "./view-scroll-gutter";

/**
 * A tabela (o `DataTable` do Prime, que rola por dentro), a barra horizontal, o paginador e a calha vertical, dentro da
 * moldura. A calha vem por último (a ordem de foco: tabela, horizontal, paginador, vertical); a grade a põe à direita
 * do corpo.
 */
export function HubTableTable() {
  const {
    slots,
    classNames,
    tableRef,
    verticalGutterRef,
    onTableKeyDown,
    partTestId,
    hasHeaderTooltip,
    tableKey,
    headerTooltipTarget,
    isLoading,
    tableProps,
    columns,
    showPaginator,
    paginatorProps,
    table,
    totalRecords,
    onPageChange,
  } = useHubTableContext();

  return (
    <>
      <HubTableLoadingAnnouncement />
      <div
        ref={tableRef}
        className={joinClassNames(styles.table, classNames.table)}
        onKeyDown={onTableKeyDown}
        data-testid={partTestId("table")}
      >
        {/* O Tooltip liga os eventos só nos alvos que existem ao montar: a `key` o remonta com a tabela. */}
        {hasHeaderTooltip ? (
          <Tooltip
            key={`tooltip:${tableKey}`}
            target={headerTooltipTarget}
            position="right"
            event="both"
          />
        ) : null}
        <DataTable
          key={`table:${tableKey}`}
          emptyMessage={isLoading ? <></> : (slots.empty ?? <HubTableEmptyDefault />)}
          loadingIcon={<Loading size="table" aria-hidden />}
          {...tableProps}
          className={joinClassNames(
            tableProps.className,
            isLoading ? styles.tableLoading : undefined,
          )}
        >
          {columns.map(({ key, tooltip, props }) => (
            <PrimeColumn
              key={key}
              {...props}
              header={
                tooltip ? (
                  <span
                    className={styles.headerTip}
                    tabIndex={0}
                    aria-label={tooltip.text}
                    data-pr-tooltip={tooltip.text}
                    data-testid={tooltip.testId}
                  >
                    {tooltip.label}
                  </span>
                ) : (
                  props.header
                )
              }
            />
          ))}
        </DataTable>
      </div>
      <HubTableScrollGutter orientation="horizontal" />
      {showPaginator ? (
        <Paginator
          {...paginatorProps}
          className={joinClassNames(classNames.paginator, paginatorProps?.className)}
          data-testid={partTestId("paginator")}
          first={table.first}
          rows={table.rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={table.rowsOptions}
          onPageChange={onPageChange}
        />
      ) : null}
      <div
        ref={verticalGutterRef}
        className={styles.gutterVertical}
        data-testid={partTestId("gutter-vertical")}
      >
        <HubTableScrollGutter orientation="vertical" />
      </div>
    </>
  );
}
HubTableTable.displayName = "HubTable.Table";
